import React, { useState } from 'react'
import { IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { useTranslation } from 'react-i18next'
import SignupSubmitButton from './SignupSubmitButton'

interface DetailsStepProps {
    email: string
    nameGiven: string
    nameFamily: string
    password: string
    minPasswordLength: number
    onNameGivenChange: (value: string) => void
    onNameFamilyChange: (value: string) => void
    onPasswordChange: (value: string) => void
    nameGivenErrorMessage?: string
    nameFamilyErrorMessage?: string
    passwordErrorMessage?: string
    submitting: boolean
}

const DetailsStep: React.FC<DetailsStepProps> = ({
    email,
    nameGiven,
    nameFamily,
    password,
    minPasswordLength,
    onNameGivenChange,
    onNameFamilyChange,
    onPasswordChange,
    nameGivenErrorMessage,
    nameFamilyErrorMessage,
    passwordErrorMessage,
    submitting,
}) => {
    const { t } = useTranslation()
    const [showPassword, setShowPassword] = useState(false)

    return (
        <>
            <Typography variant="body2" align="center" color="text.secondary">
                {t('signup.email_verified', { email })}
            </Typography>
            <Stack direction="row" spacing={1}>
                <TextField
                    fullWidth
                    // eslint-disable-next-line jsx-a11y/no-autofocus -- first field of a fresh step
                    autoFocus
                    label={t('signup.first_name_label')}
                    autoComplete="given-name"
                    value={nameGiven}
                    error={!!nameGivenErrorMessage}
                    helperText={nameGivenErrorMessage}
                    onChange={(e) => onNameGivenChange(e.target.value)}
                />
                <TextField
                    fullWidth
                    label={t('signup.last_name_label')}
                    autoComplete="family-name"
                    value={nameFamily}
                    error={!!nameFamilyErrorMessage}
                    helperText={nameFamilyErrorMessage}
                    onChange={(e) => onNameFamilyChange(e.target.value)}
                />
            </Stack>
            <TextField
                fullWidth
                label={t('signup.password_label')}
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(e) => onPasswordChange(e.target.value)}
                error={!!passwordErrorMessage}
                helperText={passwordErrorMessage ?? t('signup.password_hint', { min: minPasswordLength })}
                slotProps={{
                    input: {
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    type="button"
                                    aria-label={t('signup.toggle_password_visibility')}
                                    onClick={() => setShowPassword((visible) => !visible)}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
            />
            <SignupSubmitButton disabled={submitting || !nameGiven || !nameFamily || !password}>
                {t('signup.create_button')}
            </SignupSubmitButton>
        </>
    )
}

export default DetailsStep
